import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RouteComponentProps, useHistory, useParams } from 'react-router';
import styled from 'styled-components/macro';
import { Pagination } from '@tovia/man-ui';
import { useIntl } from 'react-intl';
import PaginationModal from 'src/client/components/modals/PaginationModal';
import LoaderWrapper from 'src/client/components/Placeholders/Layouts/GalleriesPlaceholder';
import { load } from 'src/client/redux/modules/movies';
import { SORT_TYPES } from 'src/shared/constants/movies';
import { setTitle } from 'src/client/redux/modules/app';
import { GalleryCards } from 'src/client/containers/Cards/Gallery/GalleryCards';
import { useSelector, useSettingsSelector } from 'src/client/redux/modules/helpers/useSelector';
import Helmet from '../Helmet/Helmet';
import urls from 'src/shared/urls';
import { isPrerender } from 'src/client/utils/prerenderDetect';
import { CamsContext } from 'src/client/containers/Cams/CamsContextProvider';
import { MoviesHeader } from './MoviesHeader';
import { InjectedSeoText } from 'src/client/components/OnPageSeo/InjectedSeoText';
import { OnPageText } from 'src/client/components/OnPageSeo/OnPageText/OnPageText';
import { useWidePageContext } from '../App/WidePageContextProvider';

export const DEFAULT = 'default';
export const STAFF_SELECTION = 'ss';
export const PPS_MOVIES = 'pps';
export const SERIES_MOVIES = 'srs';
export const FAN_FICTION_MOVIES = 'ff';
export const METART_INTIMATE = 'intimate';

const Div = styled.div`
  .options {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 10px 0;

    .sort-dropdown {
      margin-right: 20px;
    }
  }
`;

export type MoviesContentProps = RouteComponentProps<{ sortBy?: string; page?: string }>;

type Props = MoviesContentProps & {
  pageType?:
    | typeof DEFAULT
    | typeof STAFF_SELECTION
    | typeof PPS_MOVIES
    | typeof SERIES_MOVIES
    | typeof FAN_FICTION_MOVIES
    | typeof METART_INTIMATE;
  pageTypeParams?: {
    filter?: [string];
    tabId?: number;
    intimateSelectionOnly?: boolean;
    staffSelectionOnly?: boolean;
    PPSOnly?: boolean;
  };
  basePageUrl?: string;
};

export const MoviesContent = (props: Props) => {
  const {
    pageType = DEFAULT,
    pageTypeParams = {
      showPinnedGallery: true,
      tabId: 0,
    },
    basePageUrl = '/erotic-films',
  } = props;

  const [sortBy, setSortBy] = useState<string | null>(null);
  const config = useSelector((state) => state.app.config);
  const site = useSelector((state) => state.site);
  const { pageSize, itemCount, galleries, loaded, error } = useSelector((state) => state.movies);
  const user = useSelector((state) => state.auth.user);
  const cams = useContext(CamsContext);
  const intl = useIntl();
  const params = useParams<{ sortBy: string; page: string }>();
  const history = useHistory();

  const widePage = useWidePageContext();

  useEffect(() => {
    if (error?.status === 404) {
      history.replace(urls.get.notFound);
    }
  }, [error, history]);

  const dispatch = useDispatch();

  const sortBySetting = useSettingsSelector<string>('filmsSorting');
  const movieViewType = useSettingsSelector<string>('listView');

  useEffect(() => {
    const sort = SORT_TYPES.find((type) => type.id === params.sortBy);
    const sortByValue = (sort && params.sortBy) || sortBySetting;
    setSortBy(sortByValue);
  }, [params.sortBy, sortBySetting]);

  const pageNum = useMemo(() => {
    const sort = SORT_TYPES.find((type) => type.id === params.sortBy);
    return (!sort && parseInt(params.sortBy)) || parseInt(params.page) || 1;
  }, [params.sortBy, params.page]);

  useEffect(() => {
    if (!isPrerender) {
      window.dataLayer.push({
        event: 'select_content',
        content_type: 'movies',
        item_id: pageType,
        page: pageNum || 1,
      });
    }
  }, [pageNum, pageType]);

  useEffect(() => {
    if (sortBy) {
      const loadParams = {
        sortBy,
        galleryType: 'MOVIE',
        first: pageSize,
        page: pageNum,
        ...pageTypeParams,
      };

      dispatch(load(loadParams));
    }
  }, [pageNum, dispatch, pageSize, pageType, sortBy, user, pageTypeParams]);

  const urlSortBy = sortBy === 'latest' ? '' : `/${sortBy}`;
  const newPageUrlPrefix = basePageUrl + urlSortBy;
  const setPage = (page) => history.push(`${newPageUrlPrefix}/${page}`);

  useEffect(() => {
    const sortByValue = params.sortBy || sortBySetting;

    const sort2title = {
      top: intl.formatMessage({ id: 'moviesContent.top' }),
      viewed: intl.formatMessage({ id: 'moviesContent.viewed' }),
      latest: intl.formatMessage({ id: 'moviesContent.latest' }),
      favorited: intl.formatMessage({ id: 'moviesContent.favorited' }),
      resolution: intl.formatMessage({ id: 'moviesContent.4k' }),
    };

    dispatch(setTitle(sort2title[sortByValue]));
  }, [dispatch, intl, params.sortBy, sortBySetting]);

  // Todo Create error Component
  const errorMarkup = error ? null : null;

  return (
    <div>
      <Helmet
        id="movies"
        canonicalUrl={`${window.location.origin}${basePageUrl}`}
        defaultTitle="{{siteName}}'s {{moviesCount}} Videos {{^firstPage}}(Page {{page}}){{/firstPage}} - {{domainName}}"
        variables={{
          moviesCount: itemCount,
          page: pageNum,
          domainName: site.domain,
          siteName: site.name,
          firstPage: !pageNum || pageNum === 1,
        }}
      >
        <meta property="prerender:ttl" content="86400" />
      </Helmet>
      {errorMarkup}
      <LoaderWrapper detailed={movieViewType === 'detailed'} loading={!loaded} count={pageSize}>
        <Div className="horizontal-covers">
          {!widePage && <MoviesHeader pageType={pageType} basePageUrl={basePageUrl} sortBySetting={sortBySetting} />}
          <GalleryCards
            galleries={galleries}
            showCams={!config.hasUpdates && cams.loaded}
            seoInjectedComponent={
              pageNum === 1 ? (
                <InjectedSeoText key="injected-seo-component" index={3}>
                  <OnPageText contentName="text2" />
                </InjectedSeoText>
              ) : undefined
            }
          />

          <br />
          <div className="text-center">
            <Pagination
              activePage={pageNum}
              boundaryRange={1}
              onPageChange={setPage}
              siblingRange={1}
              totalPages={Math.ceil(itemCount / pageSize)}
              pageUrlPrefix={newPageUrlPrefix}
              showPrevNext
              modal={
                <PaginationModal
                  setPage={(pageNumber) => setPage(pageNumber)}
                  totalPages={Math.ceil(itemCount / pageSize)}
                />
              }
            />
          </div>
        </Div>
      </LoaderWrapper>
    </div>
  );
};
